import { vCPUbyInstanceType } from "@/components/instance-types";
import { useAvailableRunners } from "@/hooks/use-available-runners";
import { useListEnvironmentClasses, type PlainRunnerEnvironmentClass } from "@/queries/environment-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import type { PlainProjectEnvironmentClass } from "@/queries/project-queries";
import { type PlainRunner } from "@/queries/runner-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase, RunnerCapability, type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useEffect, useState } from "react";

export type EnvironmentClassEntry = {
    clazz: PlainRunnerEnvironmentClass;
    runner: PlainRunner;
    localRunner: boolean;
    policyRestricted: boolean;
};

/**
 * Maps environment class ID to the corresponding environment class entry
 */
export type EnvironmentClassMap = Record<string, EnvironmentClassEntry>;

export function convertToProjectEnvironmentClass(
    entry: EnvironmentClassEntry,
    order: number,
): PlainProjectEnvironmentClass {
    return {
        environmentClass:
            entry.clazz.id === "local"
                ? { case: "localRunner", value: true }
                : { case: "environmentClassId", value: entry.clazz.id },
        order,
    };
}

export function useEnvironmentClassEntries({ enabled }: { enabled: boolean } = { enabled: true }) {
    const { data: policies, isPending: isPoliciesPending } = useGetOrganizationPolicies({ enabled });

    const allowLocalRunners = policies?.allowLocalRunners;

    const { data: environmentClasses, isLoading: isLoadingClasses } = useListEnvironmentClasses({
        filter: { enabled },
    });
    const { availableRunners, isLoading: isLoadingRunners } = useAvailableRunners({ enabled });

    const [environmentClassEntries, setEnvClasses] = useState<EnvironmentClassEntry[]>([]);

    const [initialized, setInitialized] = useState<boolean>(false);

    useEffect(() => {
        const classes = environmentClasses?.classes;
        if (classes === undefined || isLoadingRunners || isLoadingClasses) {
            return;
        }

        const runnersMap = new Map(availableRunners.map((r) => [r.runnerId, r]));
        const joined: EnvironmentClassEntry[] = [];
        for (const clazz of classes) {
            const runner = runnersMap.get(clazz.runnerId);
            if (runner) {
                const localRunner = runner.kind === RunnerKind.LOCAL;
                const policyRestricted = !allowLocalRunners && localRunner;

                joined.push({ clazz, runner, localRunner, policyRestricted });
            }
        }

        setEnvClasses(joined);
        setInitialized(true);
    }, [allowLocalRunners, availableRunners, environmentClasses, isLoadingClasses, isLoadingRunners, enabled]);

    return {
        isLoading: !initialized || isLoadingClasses || isLoadingRunners || isPoliciesPending,
        environmentClassEntries,
    };
}

/**
 * Queries all available environment classes, then store them in a Record
 * that maps environment class ID to the corresponding environment class
 */
export function useEnvironmentClassMap() {
    const { data: policies, isPending: isPoliciesPending } = useGetOrganizationPolicies();

    const allowLocalRunners = policies?.allowLocalRunners;

    const { data: environmentClasses, isLoading: isLoadingClasses } = useListEnvironmentClasses({
        filter: { enabled: true },
    });
    const { availableRunners, isLoading: isLoadingRunners } = useAvailableRunners();

    const [environmentClassMap, setEnvironmentClassMap] = useState<EnvironmentClassMap>({});

    const [initialized, setInitialized] = useState<boolean>(false);

    useEffect(() => {
        const classes = environmentClasses?.classes;
        if (classes === undefined || isLoadingRunners || isLoadingClasses) {
            return;
        }

        const runnersMap = new Map<string, PlainMessage<Runner>>();
        for (const runner of availableRunners) {
            runnersMap.set(runner.runnerId, runner);
        }

        const classMap: EnvironmentClassMap = {};
        for (const clazz of classes) {
            const runner = runnersMap.get(clazz.runnerId);
            if (runner) {
                const localRunner = runner.kind === RunnerKind.LOCAL;
                const policyRestricted = !allowLocalRunners && localRunner;
                classMap[clazz.id] = { clazz, runner, localRunner, policyRestricted };
            }
        }

        setEnvironmentClassMap(classMap);
        setInitialized(true);
    }, [allowLocalRunners, availableRunners, environmentClasses, isLoadingClasses, isLoadingRunners]);

    return {
        isLoading: !initialized || isLoadingClasses || isLoadingRunners || isPoliciesPending,
        environmentClasses: environmentClassMap,
    };
}

export function getCompareLabel(entry: EnvironmentClassEntry) {
    const label =
        `${entry?.runner?.name || ""},${entry?.clazz?.displayName}, ${entry?.clazz?.description}`.toLowerCase();
    return label;
}

export function getEnvironmentClassStatus(environmentClassEntry: EnvironmentClassEntry) {
    const policyRestricted = environmentClassEntry.policyRestricted;
    const offline =
        environmentClassEntry?.runner?.status?.phase !== RunnerPhase.ACTIVE &&
        environmentClassEntry?.runner?.status?.phase !== RunnerPhase.DEGRADED;
    const degraded = environmentClassEntry?.runner?.status?.phase === RunnerPhase.DEGRADED;
    return { policyRestricted, offline, degraded };
}

/**
 * Check if an environment class entry supports a specific runner capability.
 * Handles both numeric enum values and string enum names (e.g., "RUNNER_CAPABILITY_AGENT_EXECUTION").
 */
export function hasRunnerCapability(entry: EnvironmentClassEntry, capability: RunnerCapability): boolean {
    const capabilities = entry.runner.status?.capabilities;
    if (!capabilities) {
        return false;
    }
    
    // Check if the capability exists as a numeric value
    if (capabilities.includes(capability)) {
        return true;
    }
    
    // Check if the capability exists as a string name (e.g., "RUNNER_CAPABILITY_AGENT_EXECUTION")
    // This handles cases where toPlainMessage converts enum values to their string names
    const capabilityName = RunnerCapability[capability];
    return capabilities.some((cap) => (cap as unknown) === capabilityName || (cap as unknown) === `RUNNER_CAPABILITY_${capabilityName}`);
}

/**
 * Filter environment class entries to only include those that support the specified runner capabilities.
 */
export function filterByRunnerCapabilities(
    entries: EnvironmentClassEntry[],
    requiredCapabilities: RunnerCapability[],
): EnvironmentClassEntry[] {
    return entries.filter((entry) =>
        requiredCapabilities.every((capability) => hasRunnerCapability(entry, capability)),
    );
}

/**
 * Sort environment class entries by availability, degraded status, remote runners, and CPU.
 *
 * First level of ordering:
 * 1. Active remote runners
 * 2. Active local runners
 * 3. Degraded runners (if available)
 * 4. Offline runners
 *
 * Second level of ordering:
 * 1. CPU (if available)
 * 2. Label
 */
export function sortEnvironmentClassEntries(entries: EnvironmentClassEntry[]): EnvironmentClassEntry[] {
    const getCPU = (entry: EnvironmentClassEntry) => {
        const instanceType = entry.clazz.configuration.find((c) => c.key === "instanceType")?.value;
        return instanceType ? vCPUbyInstanceType[instanceType] : undefined;
    };
    const isAvailable = (entry: EnvironmentClassEntry) => {
        const { policyRestricted, offline } = getEnvironmentClassStatus(entry);
        return !policyRestricted && !offline;
    };
    const isDegraded = (entry: EnvironmentClassEntry) => {
        const { degraded } = getEnvironmentClassStatus(entry);
        return degraded;
    };
    const isLocal = (entry: EnvironmentClassEntry) => {
        const { runner } = entry;
        return runner?.kind === RunnerKind.LOCAL;
    };
    return entries.sort((a, b) => {
        // Order by availability
        if (isAvailable(a) && !isAvailable(b)) {
            return -1;
        }
        if (!isAvailable(a) && isAvailable(b)) {
            return 1;
        }
        // Order by degraded status
        if (!isDegraded(a) && isDegraded(b)) {
            return -1;
        }
        if (isDegraded(a) && !isDegraded(b)) {
            return 1;
        }
        // Order by remote runners (prefer remote over local)
        if (!isLocal(a) && isLocal(b)) {
            return -1;
        }
        if (isLocal(a) && !isLocal(b)) {
            return 1;
        }
        // Order active ones by CPU if available
        const aCPU = getCPU(a);
        const bCPU = getCPU(b);
        if (aCPU && bCPU) {
            return aCPU - bCPU;
        }
        // Fallback to the label if the CPU is not available
        const aLabel = getCompareLabel(a);
        const bLabel = getCompareLabel(b);
        return aLabel.localeCompare(bLabel);
    });
}
